"use client";

import { TypeTimeline, TypeTimelineItem } from "@contentful/types";
import { notEmpty } from "@yardzen/js-util";
import { Badge } from "@yardzen/ui/components/badge";
import { Container } from "@yardzen/ui/components/container";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@yardzen/ui/components/carousel";
import { H1 } from "@yardzen/ui/components/h1";
import { H3 } from "@yardzen/ui/components/h3";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { resolveColorVariant } from "../theme/resolveColorVariant";

export function Timeline({
  timeline,
}: {
  timeline: TypeTimeline<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const { heading, subheading, timelineSegments, colorVariant } =
    timeline.fields ?? {};

  const v = resolveColorVariant(colorVariant);
  const hasBg = Boolean(v.bg);

  const [chosenTimeLineIndex, setTimelineIndex] = useState(0);
  const chooseTimeLine = (index: number) => {
    setTimelineIndex(index);
  };

  const TimeLineItemContent = (
    timeLineItem: TypeTimelineItem<"WITHOUT_UNRESOLVABLE_LINKS">,
    i: number,
  ) => {
    const isChoosen = chosenTimeLineIndex === i;
    return (
      <div
        className={cn(
          "mx-auto flex w-full max-w-[280px] transform flex-col justify-items-center duration-150 ease-in-out md:mx-0 md:w-72 md:max-w-none md:pt-2",
          { "md:scale-[1.03]": isChoosen },
        )}
        onMouseEnter={() => chooseTimeLine(i)}
        onClick={() => chooseTimeLine(i)}
      >
        {timeLineItem.fields.lineSegmentText && (
          <div className="mb-3 text-center">
            <Badge
              className={cn(
                "mx-auto mt-2 inline-block text-center font-mono text-[10px] font-semibold uppercase tracking-wider",
                v.badge,
              )}
              variant="secondary"
              hover={false}
            >
              {timeLineItem.fields.lineSegmentText}
            </Badge>
          </div>
        )}

        {timeLineItem?.fields.image && (
          <div
            className={cn(
              "flex flex-1 flex-col justify-start rounded-lg border-none p-4 no-underline shadow-none",
              v.card,
            )}
          >
            <Image
              src={`https:${timeLineItem?.fields.image?.fields.image?.fields.file?.url}`}
              alt={timeLineItem?.fields.image?.fields.description ?? ""}
              height={300}
              width={300}
              className="aspect-[4/3] w-full rounded-lg object-cover"
            />
            {/* The copy block used to be a fixed h-52/h-44, which reserved the
                tallest card's height under every card and left a hole under the
                short ones. The cards stretch to a shared height instead, so the
                space is only where the text actually is. */}
            <div>
              <H3 className={cn("mt-2 text-2xl sm:text-xl", v.text)}>
                {timeLineItem?.fields.image?.fields.label ?? ""}
              </H3>
              <div className="mt-2">
                <P className={cn("sm:text-sm", v.text)}>
                  {timeLineItem?.fields.image?.fields.description ?? ""}
                </P>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn("pb-4 md:pb-6", v.bg, hasBg && "pt-8 md:pt-12")}>
      <Container>
        <div className="flex flex-col items-center pb-8 text-center">
          {heading && (
            <H1
              className={cn(
                "w-[90%] px-4 pt-12 text-3xl md:w-[70%] md:text-4xl",
                v.text,
              )}
            >
              {heading}
            </H1>
          )}
          {subheading && (
            <P
              className={cn(
                "w-[90%] text-base md:min-w-[37%] md:max-w-[65%]",
                v.text,
              )}
            >
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => (
                    <Link
                      href={href ?? "#"}
                      style={{ textDecoration: "underline" }}
                    >
                      {children}
                    </Link>
                  ),
                  p: ({ children }) => <span>{children}</span>,
                }}
              >
                {subheading}
              </ReactMarkdown>
            </P>
          )}
        </div>
        <div className="mb-2 hidden flex-row justify-center gap-3 md:flex">
          {timelineSegments &&
            timelineSegments.filter(notEmpty).map((timeLineItem, i) => (
              <React.Fragment key={`d-timeline-${timeLineItem?.sys.id}`}>
                {i > 0 && (
                  <svg
                    aria-hidden
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className={cn("mt-40 h-8 w-8 shrink-0 self-start", v.text)}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12h15m0 0l-6-6m6 6l-6 6"
                    />
                  </svg>
                )}
                {TimeLineItemContent(timeLineItem, i)}
              </React.Fragment>
            ))}
        </div>
        <div className="md:hidden">
          <Carousel className="px-4">
            <CarouselContent className="pb-6" aria-live="off">
              {timelineSegments &&
                timelineSegments.filter(notEmpty).map((timeLineItem, i) => (
                  <CarouselItem
                    key={`m-timeline-${timeLineItem?.sys.id}`}
                    className="basis-9/12 sm:max-w-[320px]"
                  >
                    <>{TimeLineItemContent(timeLineItem, i)}</>
                  </CarouselItem>
                ))}
            </CarouselContent>
          </Carousel>
        </div>
      </Container>
    </div>
  );
}
